import type { ReactNode } from 'react';
import { Button, Modal, StatusBadge } from '@/components/ui';
import { formatDZD } from '@/utils/algeria';
import type { StatusKey } from '@/types';

export interface DetailLigne {
  designation: string;
  unite?: string | null;
  quantite?: number | null;
  quantite_recue?: number | null;
  prix_unitaire?: number | null;
  montant?: number | null;
}

export interface DetailDoc {
  titre: string;
  reference?: string | null;
  statut?: { label: string; key: StatusKey } | null;
  entete: { label: string; value: ReactNode }[];
  lignes: DetailLigne[];
  /** Affiche la colonne « Reçu » (bon de réception). */
  avecRecu?: boolean;
  /** Affiche les colonnes prix unitaire / montant (DA, BC). */
  avecMontants?: boolean;
  totaux?: { ht: number; tva: number; ttc: number; tva_pct?: number } | null;
}

/**
 * Vue détail commune (demande d'achat / bon de commande / bon de réception) :
 * en-tête + lignes + totaux. Même structure de données que le PDF officiel.
 */
export function DetailDocumentModal({ open, onClose, doc }: { open: boolean; onClose: () => void; doc: DetailDoc | null }) {
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={doc?.titre ?? 'Détail'} size="xl"
      footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
      {!doc ? null : (
        <div className="space-y-5">
          {/* En-tête : référence + statut */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
            <span className="text-lg font-bold text-content-primary">{doc.reference || <span className="italic text-content-muted">Brouillon (sans n°)</span>}</span>
            {doc.statut && <StatusBadge status={doc.statut.key} label={doc.statut.label} />}
          </div>

          {/* Bloc en-tête : paires libellé / valeur */}
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {doc.entete.map((e, i) => (
              <div key={i} className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-content-muted">{e.label}</dt>
                <dd className="text-sm text-content-primary">{e.value || '—'}</dd>
              </div>
            ))}
          </dl>

          {/* Lignes */}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-primary/40 text-left text-xs uppercase text-content-muted">
                  <th className="px-3 py-2">Désignation</th>
                  <th className="px-3 py-2">Unité</th>
                  <th className="px-3 py-2 text-right">Quantité</th>
                  {doc.avecRecu && <th className="px-3 py-2 text-right">Reçu</th>}
                  {doc.avecMontants && <th className="px-3 py-2 text-right">P.U. HT</th>}
                  {doc.avecMontants && <th className="px-3 py-2 text-right">Montant HT</th>}
                </tr>
              </thead>
              <tbody>
                {doc.lignes.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-content-muted">Aucune ligne.</td></tr>
                ) : doc.lignes.map((l, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-1.5 font-medium text-content-primary">{l.designation}</td>
                    <td className="px-3 py-1.5 text-content-secondary">{l.unite || '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{l.quantite ?? '—'}</td>
                    {doc.avecRecu && <td className="px-3 py-1.5 text-right tabular-nums">{l.quantite_recue ?? 0}</td>}
                    {doc.avecMontants && <td className="px-3 py-1.5 text-right tabular-nums">{l.prix_unitaire != null ? formatDZD(l.prix_unitaire) : '—'}</td>}
                    {doc.avecMontants && <td className="px-3 py-1.5 text-right tabular-nums">{l.montant != null ? formatDZD(l.montant) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totaux */}
          {doc.totaux && (
            <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-content-secondary">Total HT</span><span className="tabular-nums font-medium">{formatDZD(doc.totaux.ht)}</span></div>
              <div className="flex justify-between"><span className="text-content-secondary">TVA{doc.totaux.tva_pct != null ? ` (${(doc.totaux.tva_pct * 100).toFixed(0)}%)` : ''}</span><span className="tabular-nums font-medium">{formatDZD(doc.totaux.tva)}</span></div>
              <div className="flex justify-between border-t border-border pt-1"><span className="font-bold text-content-primary">Total TTC</span><span className="tabular-nums font-bold text-brand-primary">{formatDZD(doc.totaux.ttc)}</span></div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
